import * as React from 'react';

import type {GetTeams_teams as Team} from 'nutshell-graphql-types';

import {useActivityModalContext} from '../../activity-modal-context';

import {DateTimeSection, LocationSection, ParticipantsSection, AgendaSection} from '../calendar';
import {VideoConferencingSection} from '../calendar/video-conferencing-section';
import {ActivityTypeAndLeadsSection, FollowUpBanner} from '../internal-notes';
import {NoteSection} from '../internal-notes/note-section';

type Props = {
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    logOnCreate: boolean;
    teams: Team[] | null | undefined;
};

export function Create(props: Props) {
    const {
        calendarState,
        calendarHandlers,
        currentActivityType,
        handleSetActivityType,
        selectedRelatedEntities,
        setSelectedRelatedEntities,
        potentialRelatedEntities,
        potentialParticipants,
        previousActivity,
        quillDelta,
        handleSetLogNote,
    } = useActivityModalContext();

    const handleChange = (changeFunction: () => void) => {
        props.setHasUnsavedChanges(true);
        changeFunction();
    };

    return (
        <div className='flex flex-dir-col full-width'>
            {previousActivity && <FollowUpBanner previousActivity={previousActivity} />}
            <div className='full-width full-height pad-16 border-box overflow-auto align-self-end flex flex-dir-col gap-16'>
                <ActivityTypeAndLeadsSection
                    currentActivityType={currentActivityType}
                    onActivityTypeChange={(value) =>
                        handleChange(() => handleSetActivityType(value))
                    }
                    selectedRelatedEntities={selectedRelatedEntities}
                    onRelatedLeadsChange={(value) =>
                        handleChange(() => setSelectedRelatedEntities(value))
                    }
                    potentialRelatedEntities={potentialRelatedEntities}
                />
                {props.logOnCreate && (
                    <NoteSection
                        quillDelta={quillDelta}
                        teams={props.teams}
                        onChange={(value) => {
                            handleChange(() => handleSetLogNote(value));

                            return Promise.resolve();
                        }}
                        showSave={false}
                        cleanupOnUnmount={false}
                    />
                )}
                <DateTimeSection
                    startTime={calendarState.startTime}
                    endTime={calendarState.endTime}
                    isAllDay={calendarState.isAllDay}
                    isMultiDay={calendarState.isMultiDay}
                    onStartDateChange={(value) =>
                        handleChange(() => calendarHandlers.handleStartDateChange(value))
                    }
                    onEndDateChange={(value) =>
                        handleChange(() => calendarHandlers.handleEndDateChange(value))
                    }
                    onStartTimeChange={(value) =>
                        handleChange(() => calendarHandlers.handleStartTimeChange(value))
                    }
                    onEndTimeChange={(value) =>
                        handleChange(() => calendarHandlers.handleEndTimeChange(value))
                    }
                    onAllDayChange={(value) =>
                        handleChange(() => calendarHandlers.handleAllDayChange(value))
                    }
                />
                {NutClientConfig.hasVideoConferencingFF && (
                    <VideoConferencingSection
                        videoConferencingData={calendarState.videoConferencingData}
                        setVideoConferencingData={calendarHandlers.setVideoConferencingData}
                    />
                )}
                <LocationSection
                    location={calendarState.location}
                    onChange={(value) =>
                        handleChange(() => calendarHandlers.handleLocationChange(value))
                    }
                    meetingUrl={null}
                />
                <ParticipantsSection
                    selectedParticipants={calendarState.selectedParticipants}
                    onChange={(value) =>
                        handleChange(() => calendarHandlers.handleSelectedParticipantsChange(value))
                    }
                    potentialParticipants={potentialParticipants}
                    isReadOnly={false}
                />
                <AgendaSection
                    agenda={calendarState.agenda}
                    onChange={(value) =>
                        handleChange(() => calendarHandlers.handleAgendaChange(value))
                    }
                    forceTextfield={true}
                />
            </div>
        </div>
    );
}
